import * as React from "react"

import { CUSTOM_CSV_BROKER_SELECT_TRIGGER_CLASS } from "@/components/Broker/CustomCsvImport/customCsvWizardUi"
import { TimezoneCombobox } from "@/components/TimezoneCombobox"
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"
import type { DemoCatalogFeGuidance, DemoParseContract } from "@/types/demoEntryAnalysis"
import {
  isDemoTimezoneField,
  listDemoParseSimpleFields,
} from "@/utils/demoEntryAnalysisBrokers"
import { buildTimezoneSelectOptions } from "@/utils/ianaTimezones"

const SECTION_LABEL_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal text-[color:var(--brand-tropical-surf)]"

const AUTH_INPUT_CLASS = cn(
  "font-[family-name:var(--font-primary)]",
  "h-8 text-sm !border-border text-foreground dark:!border-border dark:!text-white dark:placeholder:!text-white/50"
)

const DEMO_TIMEZONE_TRIGGER_CLASS = cn(
  CUSTOM_CSV_BROKER_SELECT_TRIGGER_CLASS,
  "!border-border dark:!border-border dark:!text-white dark:placeholder:!text-white/50"
)

const TIMEZONE_OPTIONS = buildTimezoneSelectOptions()

export type DemoParseFieldsProps = {
  parse: DemoParseContract | null
  guidance?: DemoCatalogFeGuidance | null
  brokerName?: string | null
  values: Record<string, string>
  onChange: (formKey: string, value: string) => void
}

/** Renders catalog `demo_parse` simple fields (timezone, instrument, utc offset, …). */
export function DemoParseFields({
  parse,
  guidance,
  brokerName,
  values,
  onChange,
}: DemoParseFieldsProps): React.ReactElement | null {
  const fields = React.useMemo(
    () => listDemoParseSimpleFields(parse, guidance, brokerName),
    [parse, guidance, brokerName]
  )

  if (!fields.length) return null

  return (
    <div className="flex w-full min-w-0 flex-col gap-5" data-demo-parse-fields="">
      {fields.map(({ field, required, formKey, label }) => {
        const id = `demo-parse-${formKey}`
        if (isDemoTimezoneField(field) || formKey === "time_zone") {
          return (
            <Field key={formKey} className="gap-2">
              <FieldLabel htmlFor={id} className={SECTION_LABEL_CLASS}>
                {required ? (
                  <>
                    Timezone
                    <span className="ml-0.5 text-[#E00101]" aria-hidden>
                      *
                    </span>
                  </>
                ) : (
                  "Timezone (optional)"
                )}
              </FieldLabel>
              <FieldDescription className="font-[family-name:var(--font-primary)] text-sm font-normal text-muted-foreground">
                {label}
              </FieldDescription>
              <TimezoneCombobox
                id={id}
                value={values[formKey] || ""}
                onValueChange={(next) => onChange(formKey, next)}
                placeholder="Select timezone"
                options={TIMEZONE_OPTIONS}
                triggerClassName={DEMO_TIMEZONE_TRIGGER_CLASS}
                aria-label={label}
              />
            </Field>
          )
        }

        if (formKey === "utc_offset_hours") {
          return (
            <Field key={formKey} className="gap-2">
              <FieldLabel htmlFor={id} className={SECTION_LABEL_CLASS}>
                UTC offset hours
                {required ? (
                  <span className="ml-0.5 text-[#E00101]" aria-hidden>
                    *
                  </span>
                ) : null}
              </FieldLabel>
              <FieldDescription className="font-[family-name:var(--font-primary)] text-sm font-normal text-muted-foreground">
                {label}
              </FieldDescription>
              <Input
                id={id}
                type="number"
                step="1"
                className={AUTH_INPUT_CLASS}
                value={values[formKey] || ""}
                onChange={(e) => onChange(formKey, e.target.value)}
                placeholder="e.g. 2"
              />
            </Field>
          )
        }

        return (
          <Field key={formKey} className="gap-2">
            <FieldLabel htmlFor={id} className={SECTION_LABEL_CLASS}>
              {formKey === "import_instrument" ? "Instrument" : formKey.replace(/_/g, " ")}
              {required ? (
                <span className="ml-0.5 text-[#E00101]" aria-hidden>
                  *
                </span>
              ) : null}
            </FieldLabel>
            <FieldDescription className="font-[family-name:var(--font-primary)] text-sm font-normal text-muted-foreground">
              {label}
            </FieldDescription>
            <Input
              id={id}
              className={AUTH_INPUT_CLASS}
              value={values[formKey] || ""}
              onChange={(e) =>
                onChange(
                  formKey,
                  formKey === "import_instrument"
                    ? e.target.value.toUpperCase()
                    : e.target.value
                )
              }
              placeholder={
                formKey === "import_instrument" ? "e.g. XAUUSD" : `Enter ${formKey}`
              }
            />
          </Field>
        )
      })}
    </div>
  )
}

export function demoParseRequiredMissing(
  parse: DemoParseContract | null,
  guidance: DemoCatalogFeGuidance | null | undefined,
  values: Record<string, string>,
  brokerName?: string | null
): string | null {
  const required = listDemoParseSimpleFields(parse, guidance, brokerName).filter((f) => f.required)
  for (const row of required) {
    if (!String(values[row.formKey] || "").trim()) {
      if (row.formKey === "time_zone") return "Select a timezone to continue."
      if (row.formKey === "import_instrument") return "Enter the chart instrument to continue."
      return `Fill in ${row.formKey.replace(/_/g, " ")} to continue.`
    }
  }
  return null
}
